import Box from "@/components/Box";
import Grid from "@/components/Grid";
import { ExternalTextLink, InternalTextLink } from "@/components/Links";
import {
  CMP_PRACTITIONER_URL,
  REGENERUS_ABOUT_URL,
  REGENERUS_CONTACT_URL,
  REGENERUS_COOKIE_POLICY_URL,
  REGENERUS_PATIENT_PORTAL_URL,
  REGENERUS_PRACTITIONER_PORTAL_URL,
  REGENERUS_PRIVACY_URL,
  REGENERUS_SHIPPING_URL,
  REGENERUS_TERMS_AND_CONDITIONS_URL,
  REGENERUS_TERMS_OF_USE_URL,
  REGENERUS_VACANCIES_URL,
  SUPPLEMENTS_URL,
  TESTS_URL
} from "@/core/urls";
import usePartnerPageCheck from "@/hooks/partner/usePartnerPageCheck";
import CareQualityCommission from "@/images/CareQualityCommission";
import RegenerusLogo from "@/images/RegenerusLogoWithoutDots";
import { PanelBoxV2 } from "@/tpo/Boxes";
import Stack from "@/tpo/Stack";

import classes from "./Footer.module.css";

const SHOP_TEST_LINK = {
  label: "Tests",
  href: TESTS_URL
};

const SUPPLEMENTS_TEST_LINK = {
  label: "Supplements",
  href: SUPPLEMENTS_URL
};

const partnerLinks = [
  {
    column: "Shop",
    links: [
      SHOP_TEST_LINK,
      SUPPLEMENTS_TEST_LINK,
      {
        label: "European labs",
        href: "https://www.regeneruslabs.me/#",
        external: true
      }
    ]
  },
  {
    column: "About Us",
    links: [
      {
        label: "Our Mission",
        href: REGENERUS_ABOUT_URL,
        external: true
      },
      {
        label: "Meet the team",
        href: REGENERUS_ABOUT_URL,
        external: true
      },
      {
        label: "Vacancies",
        href: REGENERUS_VACANCIES_URL,
        external: true
      }
    ]
  },
  {
    column: "Practitioners",
    links: [
      {
        label: "Patient portal",
        href: REGENERUS_PATIENT_PORTAL_URL,
        external: true
      },
      {
        label: "Practitioner platform",
        href: REGENERUS_PRACTITIONER_PORTAL_URL,
        external: true
      },
      {
        label: "Practitioner application",
        href: CMP_PRACTITIONER_URL,
        external: false
      }
    ]
  },
  {
    column: "Policies",
    links: [
      {
        label: "Terms & Conditions",
        href: REGENERUS_TERMS_AND_CONDITIONS_URL
      },
      {
        label: "Terms of Use",
        href: REGENERUS_TERMS_OF_USE_URL
      },
      {
        label: "Shipping Policy",
        href: REGENERUS_SHIPPING_URL
      },
      {
        label: "Privacy Policy",
        href: REGENERUS_PRIVACY_URL
      },
      {
        label: "Cookie Policy",
        href: REGENERUS_COOKIE_POLICY_URL
      }
    ]
  },
  {
    column: "Help and support",
    links: [
      {
        label: "Support centre",
        href: "https://support-centre.regeneruslabs.com/",
        external: true
      },
      {
        label: "Contact us",
        href: REGENERUS_CONTACT_URL,
        external: true
      }
    ]
  }
];

const userLinks = [
  {
    column: "Shop",
    links: [SHOP_TEST_LINK]
  },
  {
    column: "About Us",
    links: [
      {
        label: "Our Mission",
        href: REGENERUS_ABOUT_URL,
        external: true
      },
      {
        label: "Meet the team",
        href: REGENERUS_ABOUT_URL,
        external: true
      },
      {
        label: "Vacancies",
        href: REGENERUS_VACANCIES_URL,
        external: true
      }
    ]
  },
  {
    column: "Platform",
    links: [
      {
        label: "Patient Portal",
        href: REGENERUS_PATIENT_PORTAL_URL,
        external: true
      },
      {
        label: "Practitioner platform",
        href: REGENERUS_PRACTITIONER_PORTAL_URL,
        external: true
      },
      {
        label: "Practitioner application",
        href: CMP_PRACTITIONER_URL,
        external: false
      }
    ]
  },
  {
    column: "Policies",
    links: [
      {
        label: "Terms & Conditions",
        href: REGENERUS_TERMS_AND_CONDITIONS_URL
      },
      {
        label: "Terms of Use",
        href: REGENERUS_TERMS_OF_USE_URL
      },
      {
        label: "Shipping Policy",
        href: REGENERUS_SHIPPING_URL
      },
      {
        label: "Privacy Policy",
        href: REGENERUS_PRIVACY_URL
      },
      {
        label: "Cookie Policy",
        href: REGENERUS_COOKIE_POLICY_URL
      }
    ]
  },
  {
    column: "Help and support",
    links: [
      {
        label: "Support centre",
        href: "https://support-centre.regeneruslabs.com/",
        external: true
      },
      {
        label: "Contact us",
        href: REGENERUS_CONTACT_URL,
        external: true
      }
    ]
  }
];

function FooterTemplate({ logo, links }) {
  return (
    <Stack flexDirection={["column", "column", "row"]} gap={80}>
      <Stack>
        <Box gridColumnStart={["1", "unset"]} gridColumnEnd={["3", "unset"]}>
          {logo}
        </Box>
        <CareQualityCommission />
      </Stack>
      <Grid
        gridTemplateColumns={[
          "repeat( auto-fit )",
          "repeat( auto-fit, minmax(120px, 1fr) )",
          "1fr 1fr 1fr 1fr 1fr"
        ]}
        gridRowGap={[50, 50, 16]}
        gridColumnGap={[50, 50, 16]}
        width="100%"
      >
        {links.map((col, idx) => (
          <Stack key={idx} gap={[20, 20, 40]}>
            <Box color="white" fontFamily="gilroyBold" fontSize={16}>
              {col.column}
            </Box>
            <Stack gap={20}>
              {col.links.map(link =>
                link.external ? (
                  <ExternalTextLink
                    color="white"
                    key={link.label}
                    href={link.href}
                    fontFamily="gilroyMedium"
                    fontSize={[14, 14, 16]}
                    underline={false}
                    className={classes.link}
                  >
                    {link.label}
                  </ExternalTextLink>
                ) : (
                  <InternalTextLink
                    color="white"
                    key={link.label}
                    href={link.href}
                    fontFamily="gilroyMedium"
                    fontSize={[14, 14, 16]}
                    underline={false}
                    className={classes.link}
                  >
                    {link.label}
                  </InternalTextLink>
                )
              )}
            </Stack>
          </Stack>
        ))}
      </Grid>
    </Stack>
  );
}

export default function Footer() {
  const isPartnerPage = usePartnerPageCheck({
    excludeApplicationPages: false
  });

  return (
    <PanelBoxV2
      maxWidth={1538}
      outer={{
        py: 40,
        bg: "dark",
        px: [20, 20, 95],
        "data-component-name": "Footer"
      }}
    >
      <FooterTemplate
        logo={<RegenerusLogo fill="white" />}
        links={isPartnerPage ? partnerLinks : userLinks}
      />
    </PanelBoxV2>
  );
}
